import classNames from 'classnames';
import PropTypes from 'prop-types';

// Audio visualization component
function ChatAudioRecorderTicks({
  audioLevels,
  isRecording,
  isPlaying,
  playProgress,
  totalTicks = 10,
  className,
  messaging,
  author,
}) {
  const tickWidth = 3;
  const tickSpacing = 2;
  const minHeight = 6;
  const maxHeight = 28;

  const totalWidth = totalTicks * (tickWidth + tickSpacing) - tickSpacing;

  // Generate tick heights based on audio levels
  const ticks = [];

  if (isRecording) {
    // During recording: show rolling ticks from right to left
    for (let i = 0; i < totalTicks; i += 1) {
      const levelIndex = audioLevels.length - totalTicks + i;
      let height = minHeight;

      if (levelIndex >= 0 && audioLevels[levelIndex] !== undefined) {
        const level = audioLevels[levelIndex];
        // Enhanced scaling for better visibility
        height = minHeight + (Math.min(level, 100) / 100) * (maxHeight - minHeight);
      }

      ticks.push(Math.max(height, minHeight));
    }
  } else {
    // During playback: show static ticks representing the full audio
    const ticksPerLevel = totalTicks / Math.max(audioLevels.length, 1);

    for (let i = 0; i < totalTicks; i += 1) {
      const levelIndex = Math.floor(i / ticksPerLevel);
      let height = minHeight;

      if (audioLevels[levelIndex] !== undefined) {
        const level = audioLevels[levelIndex];
        height = minHeight + (Math.min(level, 100) / 100) * (maxHeight - minHeight);
      }

      ticks.push(Math.max(height, minHeight));
    }
  }

  return (
    <div
      className={classNames('flex items-center justify-center', className)}
      style={{ width: `${totalWidth}px`, height: `${maxHeight}px` }}
    >
      {ticks.map((height, index) => {
        const isPlayed = isPlaying && (index / totalTicks) <= playProgress;

        // Enhanced visual feedback
        let tickClass = 'transition-all duration-150 rounded-full';
        let opacity;

        if (isRecording) {
          // During recording, show active ticks with full opacity
          if (index >= totalTicks - audioLevels.length) {
            opacity = 1;
            tickClass += ' bg-pxWarning'; // Active recording color
          } else {
            opacity = 0.3;
            tickClass += ' bg-pxMedBlue'; // Inactive ticks
          }
        } else if (isPlaying) {
          // During playback
          if (messaging) {
            if (isPlayed) {
              opacity = 1;
              tickClass += (author) ? ' bg-msgOthersTxt' : ' bg-msgMineTxt'; // Played portion
            } else {
              opacity = 0.3;
              tickClass += (author) ? ' bg-msgOthersTxt' : ' bg-msgMineTxt'; // Unplayed portion
            }
          } else if (isPlayed) {
            opacity = 1;
            tickClass += ' bg-pxAccentBlue'; // Played portion
          } else {
            opacity = 0.3;
            tickClass += ' bg-pxMedBlue'; // Unplayed portion
          }
        } else {
          // Static state
          if (messaging) {
            tickClass += (author) ? ' bg-msgOthersTxt' : ' bg-msgMineTxt';
          } else {
            tickClass += ' bg-pxMedBlue'; // Unplayed portion
          }
          opacity = 0.6;
        }

        return (
          <div
            key={index}
            className={tickClass}
            style={{
              width: `${tickWidth}px`,
              height: `${height}px`,
              marginRight: index < ticks.length - 1 ? `${tickSpacing}px` : 0,
              opacity,
            }}
          />
        );
      })}
    </div>
  );
}

ChatAudioRecorderTicks.propTypes = {
  audioLevels: PropTypes.arrayOf(PropTypes.number).isRequired,
  isRecording: PropTypes.bool.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  playProgress: PropTypes.number.isRequired,
  totalTicks: PropTypes.number,
  className: PropTypes.string,
  messaging: PropTypes.bool,
  author: PropTypes.string,
};

export default ChatAudioRecorderTicks;
